import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  FormOutlined,
  SearchOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";

const OrderList = ({
  searchKeyword = "",
  sortBy = "date",
  sortOrder = "desc",
  dateFilter = "",
  dateFrom = "",
  dateTo = "",
  currentPage = 1,
  onPageChange,
}) => {
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [productsPerPage] = useState(8);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [
    currentPage,
    searchKeyword,
    sortBy,
    sortOrder,
    dateFilter,
    dateFrom,
    dateTo,
  ]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Build query parameters for the new getAllInvoices API
      const params = new URLSearchParams();
      params.set("page", currentPage.toString());
      params.set("pageSize", productsPerPage.toString());

      if (searchKeyword.trim()) {
        params.set("search", searchKeyword.trim());
      }

      if (sortBy) {
        params.set("sortBy", sortBy);
      }

      if (sortOrder) {
        params.set("sortOrder", sortOrder);
      }

      if (dateFilter) {
        params.set("dateFilter", dateFilter);
      }

      if (dateFrom) {
        params.set("dateFrom", dateFrom);
      }

      if (dateTo) {
        params.set("dateTo", dateTo);
      }

      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/api/invoices/get-allInvoice?${params.toString()}`
      );

      if (response.status === 200) {
        const data = response.data;
        setProducts(data.invoices || []);
        setTotalProducts(data.totalInvoices || 0);
        setTotalPages(Math.ceil((data.totalInvoices || 0) / productsPerPage));
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setProducts([]);
      setTotalProducts(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const getStatusText = (statusOrder) => {
    if (statusOrder?.isCancel) return "Đã hủy";
    if (!statusOrder?.confirmInvoice) return "Chờ xác nhận";
    if (!statusOrder?.isShipping && statusOrder?.confirmInvoice)
      return "Chờ vận chuyển";
    if (!statusOrder?.isSuccess && statusOrder?.isShipping) return "Đang giao";
    if (statusOrder?.isSuccess && statusOrder?.isShipping)
      return "Đã giao thành công";
    return "Chưa xác định";
  };

  const getStatusBadge = (statusOrder) => {
    if (statusOrder?.isCancel) return "bg-red-100 text-red-800";
    if (!statusOrder?.confirmInvoice) return "bg-orange-100 text-orange-800";
    if (!statusOrder?.isShipping && statusOrder?.confirmInvoice)
      return "bg-yellow-100 text-yellow-800";
    if (!statusOrder?.isSuccess && statusOrder?.isShipping)
      return "bg-blue-100 text-blue-800";
    if (statusOrder?.isSuccess && statusOrder?.isShipping)
      return "bg-green-100 text-green-800";
    return "bg-gray-100 text-gray-800";
  };

  const formatCurrency = (amount) => {
    return (
      amount?.toLocaleString("vi-VN", {
        style: "currency",
        currency: "VND",
      }) || "0 ₫"
    );
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm");
    } catch (error) {
      return "Không xác định";
    }
  };

  const showPagination = totalPages > 1;

  // Calculate visible page range
  const getVisiblePages = () => {
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <div>
      {/* Results Info */}
      {!isLoading && (
        <div className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200">
          {products.length > 0 ? (
            <>
              Hiển thị {(currentPage - 1) * productsPerPage + 1} -{" "}
              {Math.min(currentPage * productsPerPage, totalProducts)} trong
              tổng số {totalProducts} đơn hàng
              {searchKeyword && (
                <span className="ml-2 text-blue-600">
                  (tìm kiếm: "{searchKeyword}")
                </span>
              )}
              {dateFilter && (
                <span className="ml-2 text-purple-600">(lọc thời gian)</span>
              )}
              {(dateFrom || dateTo) && (
                <span className="ml-2 text-purple-600">
                  (khoảng: {dateFrom || "..."} → {dateTo || "..."})
                </span>
              )}
            </>
          ) : (
            "Không có đơn hàng nào"
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2 text-gray-500">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
            <span>Đang tải dữ liệu...</span>
          </div>
        </div>
      )}

      {/* Table Section */}
      {!isLoading && (
        <>
          {products.length > 0 ? (
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      STT
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mã đơn
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Khách hàng
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Số điện thoại
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Địa chỉ
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tổng tiền
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ngày tạo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trạng thái
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                      Hành động
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((invoice, index) => (
                    <tr
                      key={invoice?.order?._id || index}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(currentPage - 1) * productsPerPage + index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {invoice?.order?.code || "N/A"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                        <div
                          className="truncate font-medium"
                          title={invoice?.order?.fullName}
                        >
                          {invoice?.order?.fullName || "Không có tên"}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {invoice?.order?.shippingPhone || "N/A"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 max-w-xs">
                        <div
                          className="truncate"
                          title={invoice?.order?.shippingAddress}
                        >
                          {invoice?.order?.shippingAddress ||
                            "Không có địa chỉ"}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(invoice?.order?.total)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(invoice?.order?.createdAt)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(
                            invoice?.statusOrder
                          )}`}
                        >
                          {getStatusText(invoice?.statusOrder)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-3">
                          <Link
                            to={`/admin/order/detail?id=${
                              invoice?.order?.code?.substring(1) || ""
                            }`}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="Xem chi tiết"
                          >
                            <SearchOutlined className="text-lg" />
                          </Link>
                          {invoice?.statusOrder?.shipSuccecs === true ||
                          invoice?.statusOrder?.isCancel === true ? (
                            <span
                              className="text-gray-400 cursor-not-allowed"
                              title="Không thể chỉnh sửa"
                            >
                              <FormOutlined className="text-lg" />
                            </span>
                          ) : (
                            <Link
                              to={`/admin/order/update?id=${
                                invoice?.order?.code?.substring(1) || ""
                              }`}
                              className="text-green-600 hover:text-green-900 transition-colors"
                              title="Chỉnh sửa"
                            >
                              <FormOutlined className="text-lg" />
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📦</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Không tìm thấy đơn hàng
              </h3>
              <p className="text-gray-500">
                {searchKeyword
                  ? `Không có đơn hàng nào phù hợp với từ khóa "${searchKeyword}"`
                  : dateFilter || dateFrom || dateTo
                  ? "Không có đơn hàng nào trong khoảng thời gian này"
                  : "Chưa có đơn hàng nào trong hệ thống"}
              </p>
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {showPagination && !isLoading && (
        <div className="flex items-center justify-between mt-6 px-4 py-3 bg-white border border-gray-200 rounded-lg">
          <div className="text-sm text-gray-700">
            Trang {currentPage} / {totalPages} ({totalProducts} đơn hàng)
          </div>

          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            {currentPage > 1 && (
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
              >
                <LeftOutlined className="mr-1" />
                Trước
              </button>
            )}

            {/* First page */}
            {currentPage > 3 && (
              <>
                <button
                  onClick={() => handlePageChange(1)}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
                >
                  1
                </button>
                {currentPage > 4 && (
                  <span className="px-2 text-gray-500">...</span>
                )}
              </>
            )}

            {/* Page Numbers */}
            <div className="flex space-x-1">
              {getVisiblePages().map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    currentPage === page
                      ? "bg-blue-600 text-white border border-blue-600"
                      : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700"
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>

            {/* Last page */}
            {currentPage < totalPages - 2 && (
              <>
                {currentPage < totalPages - 3 && (
                  <span className="px-2 text-gray-500">...</span>
                )}
                <button
                  onClick={() => handlePageChange(totalPages)}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
                >
                  {totalPages}
                </button>
              </>
            )}

            {/* Next Button */}
            {currentPage < totalPages && (
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
              >
                Sau
                <RightOutlined className="ml-1" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderList;
